import {
  PredictionResponse,
  RulPredictionResponse,
  VehicleData,
  VinPredictionRequest,
  VinRulRequest,
  useLazyCheckVinAvailabilityQuery,
  usePredictByVinMutation,
  usePredictByVinRulMutation,
} from '@mira/api';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useOnMount} from 'shared/web';

import {REPORT_WINDOW_ID} from '../constants/reportWindow';

interface UseVinPredictionFlowArgs {
  vin: string | undefined;
  vehicleData?: VehicleData;
}

export interface UseVinPredictionFlowResult {
  predictionData: PredictionResponse | undefined;
  rulData: RulPredictionResponse | null | undefined;
  isLoading: boolean;
  error: unknown;
  /** `true` when the VIN check returned `exists=false`. Distinct from `error`. */
  notFound: boolean;
}

export function useVinPredictionFlow(args: UseVinPredictionFlowArgs): UseVinPredictionFlowResult {
  const [predictionData, setPredictionData] = useState<PredictionResponse | undefined>(undefined);
  const [rulData, setRulData] = useState<RulPredictionResponse | null | undefined>(undefined);
  const [flowError, setFlowError] = useState<unknown>(undefined);
  const [isFlowRunning, setIsFlowRunning] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const rulRequestGeneration = useRef(0);

  const [checkVin] = useLazyCheckVinAvailabilityQuery();
  const [predictByVin] = usePredictByVinMutation();
  const [predictByVinRul] = usePredictByVinRulMutation();

  const handlePredictionSuccess = (response: PredictionResponse) => {
    setPredictionData(response);
    setIsFlowRunning(false);
  };

  const startRulRequest = () => {
    rulRequestGeneration.current += 1;
    setRulData(undefined);

    return rulRequestGeneration.current;
  };

  const invalidateRulRequest = () => {
    rulRequestGeneration.current += 1;
    setRulData(null);
  };

  const handleNotFound = () => {
    invalidateRulRequest();
    setNotFound(true);
    setIsFlowRunning(false);
  };

  const handleError = (err: unknown) => {
    invalidateRulRequest();
    setFlowError(err);
    setIsFlowRunning(false);
  };

  const handleRulSuccess = (generation: number, response: RulPredictionResponse) => {
    if (generation === rulRequestGeneration.current) {
      setRulData(response);
    }
  };

  // RUL is a non-fatal parallel enrichment stream. A failure must degrade the
  // report silently (clear stale data) instead of surfacing a user-facing error,
  // so it deliberately bypasses the standard `handleApiError` convention.
  const handleRulError = (generation: number) => {
    if (generation === rulRequestGeneration.current) {
      setRulData(null);
    }
  };

  useOnMount(() => {
    if (isNil(args.vin)) {
      setIsFlowRunning(false);
      invalidateRulRequest();
      return;
    }

    const vin = args.vin;
    checkVin({vin})
      .unwrap()
      .then((vinCheck) => {
        if (!vinCheck.exists) {
          handleNotFound();
          return;
        }

        const vinPredictionRequest: VinPredictionRequest = {
          vin,
          windows: [REPORT_WINDOW_ID],
        };
        const vinRulRequest: VinRulRequest = {vin};

        if (isNotNil(args.vehicleData?.car_style)) {
          vinPredictionRequest.car_style = args.vehicleData.car_style;
          vinRulRequest.car_style = args.vehicleData.car_style;
        }
        if (isNotNil(args.vehicleData?.mileage)) {
          vinRulRequest.mileage = args.vehicleData.mileage;
        }

        predictByVin({vinPredictionRequest})
          .unwrap()
          .then(handlePredictionSuccess)
          .catch(handleError);
        const rulGeneration = startRulRequest();
        predictByVinRul({vinRulRequest})
          .unwrap()
          .then((response) => handleRulSuccess(rulGeneration, response))
          .catch(() => handleRulError(rulGeneration));
      })
      .catch(handleError);
  });

  return {
    predictionData,
    rulData,
    isLoading: isFlowRunning,
    error: flowError,
    notFound,
  };
}
